import {
  WeatherAttribution,
  WeatherCondition,
  WeatherForecastPeriod,
  WeatherMode,
  WeatherStatus,
} from "./weatherService";

export type { WeatherMode } from "./weatherService";

const SITE_BASE = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/\/$/, "");
const API_BASE = SITE_BASE.endsWith("/api") ? SITE_BASE : `${SITE_BASE}/api`;

export type PlannerMode = "hotel" | "category";
export type PlannerPace = "relaxed" | "normal" | "packed";

export interface CityOption {
  id: number;
  name: string;
  place_count?: number;
}

export interface CategoryOption {
  id: number;
  name: string;
  place_count?: number;
}

export interface HotelOption {
  place_id: string;
  name: string;
  address?: string | null;
  rate?: number | null;
}

export interface PlannerPlaceCardDetails {
  imageUrl?: string;
  city?: string;
  description?: string;
  reviewCount?: number;
}

export interface PlannerBaseRequest {
  city: string;
  days: number;
  num_alternatives: number;
  pace: PlannerPace;
  min_rating: number;
  start_date?: string;
  weather_mode: WeatherMode;
  user_lat?: number;
  user_lon?: number;
  max_distance?: number;
}

export interface HotelPlannerRequest extends PlannerBaseRequest {
  hotel_place_id?: string | null;
}

export interface CategoryPlannerRequest extends PlannerBaseRequest {
  categories: string[];
}

export interface PlannerPlace {
  place_id: string;
  name: string;
  rate: number | null;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  categories?: Array<{ id: number; name: string }> | null;
}

export interface PlannerStop {
  order: number;
  slot: string;
  label: string;
  distance_from_prev_km: number;
  score: number;
  place: PlannerPlace;
}

export interface PlannerWeather {
  date: string;
  temperature_min_c: number;
  temperature_max_c: number;
  condition: WeatherCondition;
  periods: WeatherForecastPeriod[];
  attribution: WeatherAttribution;
}

export interface PlannerDay {
  day: number;
  date?: string;
  weather_status?: WeatherStatus;
  weather?: PlannerWeather | null;
  weather_warnings?: string[];
  hotel: PlannerPlace | null;
  total_distance_km: number;
  total_score: number;
  stops: PlannerStop[];
}

export interface PlannerAlternative {
  index: number;
  total_score: number;
  total_distance_km: number;
  days: PlannerDay[];
}

export class PlannerApiError extends Error {
  fields?: Record<string, unknown>;

  constructor(message: string, fields?: Record<string, unknown>) {
    super(message);
    this.name = "PlannerApiError";
    this.fields = fields;
  }
}

async function parseResponse(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    throw new PlannerApiError("پاسخ نامعتبر از سرور دریافت شد. لطفاً دوباره تلاش کنید.");
  }
}

export async function fetchPlannerCities(signal?: AbortSignal): Promise<CityOption[]> {
  const response = await fetch(`${API_BASE}/cities/active/`, { signal });
  const body = await parseResponse(response);
  if (!response.ok) throw new PlannerApiError("دریافت فهرست شهرها ناموفق بود.");
  return Array.isArray(body) ? body : body.data || [];
}

export async function fetchPlannerCategories(signal?: AbortSignal): Promise<CategoryOption[]> {
  const response = await fetch(`${API_BASE}/categories/`, { signal });
  const body = await parseResponse(response);
  if (!response.ok || body.success === false) {
    throw new PlannerApiError(body.error?.message || "دریافت فهرست دسته‌بندی‌ها ناموفق بود.");
  }
  return Array.isArray(body) ? body : body.data || [];
}

export async function fetchPlannerHotels(city: string, signal?: AbortSignal): Promise<HotelOption[]> {
  const params = new URLSearchParams({ city, planner_role: "accommodation", page_size: "50" });
  const response = await fetch(`${API_BASE}/places/?${params.toString()}`, { signal });
  const body = await parseResponse(response);
  if (!response.ok) throw new PlannerApiError("دریافت اقامتگاه‌ها ناموفق بود.");
  return Array.isArray(body) ? body : body.data || [];
}

export async function fetchPlannerPlaceCardDetails(
  placeId: string,
  signal?: AbortSignal,
): Promise<PlannerPlaceCardDetails> {
  const response = await fetch(`${API_BASE}/places/${placeId}/`, { signal });
  const body = await parseResponse(response);
  if (!response.ok) return {};
  const place = body.data || body;
  const fileUrl = place.images?.[0]?.file_url;
  const imageUrl = fileUrl
    ? fileUrl.startsWith("http")
      ? fileUrl
      : `${SITE_BASE}${fileUrl.startsWith("/") ? "" : "/"}${fileUrl}`
    : undefined;
  return {
    imageUrl,
    city:
      place.cities?.[0]?.name ||
      (typeof place.city === "string" ? place.city : place.city?.name) ||
      undefined,
    description: place.description || undefined,
    reviewCount: place.review_count || 0,
  };
}

export async function generatePlan(
  mode: PlannerMode,
  payload: HotelPlannerRequest | CategoryPlannerRequest,
  signal?: AbortSignal,
): Promise<PlannerAlternative[]> {
  const path = mode === "hotel" ? "/plans/hotel-based/" : "/plans/category-based/";
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    // Planner generation is public. Omitting cookies avoids a CSRF check when
    // a browser happens to have an unrelated authenticated session.
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });
  const body = await parseResponse(response);
  if (!response.ok || body.success === false) {
    throw new PlannerApiError(
      body.error?.message || "ساخت برنامه سفر ناموفق بود. لطفاً دوباره تلاش کنید.",
      body.error?.fields,
    );
  }
  return body.data || [];
}
